import React, { FC, useEffect, useRef, useState } from "react";
import "./Calculator.css";
import { centered, flexer } from "../../styles/globalStyles";
import SelectField from "../../components/SelectField";
import StatesAndCities from "../../constants/data";
import { TbCurrencyNaira, TbCurrencyDollar } from "react-icons/tb";
import TownHouse2 from "../../assets/svg/town-house-2";
import TownHouse from "../../assets/svg/town-house";
import Bungalow from "../../assets/svg/bungalo";
import Bungalow2 from "../../assets/svg/bungalo-2";
import Duplex from "../../assets/svg/duplex";
import Duplex2 from "../../assets/svg/duplex-2";
import InputField from "../../components/InputField";
import { Controller, useForm } from "react-hook-form";
import Button from "../../components/Button";
import { yupResolver } from "@hookform/resolvers/yup";
import { calculatorSchema } from "../../validation/index.validation";
import useFetch from "../../hooks/useLoading";
import { convertCurrency, EstimationApi } from "../../API";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const CurrentBtnStyle =
  "p-1  border-2 border-gray-500 hover:opacity-90 rounded-full cursor-pointer";

type BuildingHouse = "bungalow" | "duplex" | "terrace";
interface State {
  state: string;
  livingRoom: string;
  bedRoom: string;
  kitchen: string;
  toiletBath: string;
  currency?: "naira" | "dollar";
  type: BuildingHouse;
}

const initialState: State = {
  type: "bungalow",
  livingRoom: "",
  toiletBath: "",
  bedRoom: "",
  kitchen: "",
  state: "",
};

export interface Building {
  divKey: string;
  Svg: FC<{ className?: string }>;
  Svg2: FC<{ className?: string }>;
  label: string;
  labelKey: BuildingHouse;
}
export interface Dimensions {
  units: number;
  size: number;
}

export type DimType = "sqm" | "sqft";

export interface Modal {
  status: boolean;
  label?: string;
  key?: keyof State;
}

export default function Calculator() {
  const { isLoading, load } = useFetch();
  const [range, setRange] = useState("0");
  const [isValid, setValid] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimType, setDimType] = useState<DimType>("sqm");
  const [dimensions, setDimensions] = useState<{
    [key: string]: Dimensions[];
  }>({});
  const [estimation, setEstimation] = useState(0);
  const [currency, setCurrency] = useState<"naira" | "dollar">("naira");

  const {
    reset,
    watch,
    control,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<State>({
    reValidateMode: "onChange",
    defaultValues: initialState,
    resolver: yupResolver(calculatorSchema),
  });

  useEffect(() => {
    const id = setInterval(() => {
      let isComplete = true;

      const currentData: any = getValues();

      for (const value of Object.values(currentData)) {
        if (!value) {
          isComplete = false;
          break;
        }
      }

      setValid((prev) => (prev !== isComplete ? !prev : prev));
    }, 500);
    return () => {
      clearInterval(id);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (() => {
      if (!estimation) {
        setRange("0");
        return null;
      }

      switch (currency) {
        case "dollar":
          (async () => {
            setRange("Converting.... to");
            const res = await convertCurrency(estimation);
            const min = Number((res.result * 0.95).toFixed(2)).toLocaleString();
            const max = Number((res.result * 1.05).toFixed(2)).toLocaleString();
            setRange(`${min} - ${max}`);
          })();
          break;
        case "naira":
          (() => {
            const min = Number((estimation * 0.95).toFixed(0)).toLocaleString();
            const max = Number((estimation * 1.05).toFixed(0)).toLocaleString();
            setRange(`${min} - ${max}`);
          })();
          break;
        default:
          break;
      }
    })();
  }, [estimation, currency]);

  const toggleCurrency = () => {
    setCurrency(currency === "naira" ? "dollar" : "naira");
  };

  const toggleDimType = () => {
    setDimType((prev) => (prev === "sqm" ? "sqft" : "sqm"));
  };

  const resetForm = () => {
    setValid(false);
    setEstimation(0);
    reset(initialState);
  };

  const submitHandler = handleSubmit((data) => {
    type Payload = {
      name: string;
      access_token: string;
      unit_of_measurement?: DimType;
      components: { [key: string]: { units: number; size: number }[] };
    };

    const payload: Payload = {
      name: data.type,
      components: {},
      access_token: "BNKLECALCACCESSVALKEYTOKEN",
      unit_of_measurement: dimType === "sqm" ? undefined : "sqft",
    };

    for (const [key, value] of Object.entries(data)) {
      const dims = dimensions[key];

      if (key === "state" || key === "type") continue;

      const keyName = () => {
        switch (key as keyof State) {
          case "bedRoom":
            return "bed_room";
          case "livingRoom":
            return "living_room";
          case "kitchen":
            return "kitchen";
          case "toiletBath":
            return "toilet";
          default:
            return "";
        }
      };

      /** use dimensions if available */
      payload.components[keyName()] = dims || [
        { units: Number(value), size: 0 },
      ];
    }

    load(EstimationApi(payload))
      .then((res) => {
        if (containerRef.current) {
          // scroll to top
          containerRef.current.scrollTo(0, 0);
        }
        if (res.message) {
          toast.success(res.message);
          setEstimation(res.data.estimate);
        }
      })
      .catch((err) => {
        if (err.message || err.error) {
          toast.error(err.message || err.error);
        }
      });
  });

  const Building = ({ divKey, Svg, Svg2, label, labelKey }: Building) => (
    <Controller
      control={control}
      name="type"
      render={({ field: { onChange, value } }) => (
        <div
          key={divKey}
          style={{ flex: ".3" }}
          onClick={() => {
            setEstimation(0);
            onChange(labelKey);
          }}
          className={
            centered +
            `flex-col cursor-pointer pt-6 pb-2 rounded-md border 
          ${
            value === labelKey ? "bg-PRIMARY hover:bg-PRIMARY text-white" : null
          } 
          ${
            value !== labelKey &&
            "hover:bg-blue-100 text-gray-700 hover:text-PRIMARY house"
          }
          hover:border-PRIMARY
        `
          }
        >
          {value === labelKey ? (
            <Svg2 className="w-18 h-full object-cover mb-1 bg-blend-screen" />
          ) : (
            <Svg className="w-18 h-full object-cover mb-1 bg-blend-screen" />
          )}
          <label className={`cursor-pointer capitalize`}>{label}</label>
        </div>
      )}
    />
  );

  return (
    <div
      ref={containerRef}
      className={
        "grid items-start xl:items-center scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50 scrollbar-thumb-rounded-full justify-around xl:flex xl:h-full h-screen flex-col md:flex-row overflow-y-auto"
      }
    >
      <section
        className={
          centered + "flex-col xl:items-start max-w-lg xl:max-w-md p-10 xl:p-0 "
        }
      >
        <strong className="bg-green-500 text-white p-2 rounded-full xl:mt-0">
          Cost Calculator
        </strong>
        <p className="font-Bold text-3xl text-center xl:text-left md:text-5xl mt-5 xl:mt-10">
          Bnklé lets you build your dream home from anywhere.
        </p>
        <div className="mt-32 text-center lg:text-left">
          <strong className="text-sm text-center lg:text-left">
            Disclaimer
          </strong>
          <p className="text-LIGHT_TEXT text-sm text-justify lg:text-left">
            <br />
            Please use this tool as a starting point in your planning process.
            It's important to note that building costs can vary based on a
            several factors. <br />
            <br />
            Caveat: Our cost estimator is designed to give you a general, rather
            than precise idea of the cost of building a house. It does not
            account for all possible variables, such as external works, choice
            of material, regional differences in material costs, labor rates, or
            unforeseen site conditions. The actual cost of your project may
            differ from the estimate provided. For an accurate estimate, we
            recommend signing up and engaging with a{" "}
            <Link
              target="_self"
              to={`//auth.${process.env.REACT_APP_URL}/select-persona?type=signup`}
              className="text-black underline hover:opacity-80 cursor-pointer"
            >
              construction professional
            </Link>{" "}
            on the platform.
          </p>
        </div>
      </section>
      <section className={"relative w-full xl:w-2/5 xl:max-w-xl " + centered}>
        <div className="background-animate z-0" />
        <form
          onSubmit={submitHandler}
          onChange={() => {
            setEstimation(0);
          }}
          className="h-fit xl:w-4/5 w-11/12 bg-white px-8 py-7 rounded-md relative z-10"
        >
          <div className="bg-gray-100 px-5 py-3 rounded-md">
            <div className="flex items-center">
              <div
                onClick={toggleCurrency}
                className={`${CurrentBtnStyle} ${
                  currency === "naira" && "bg-gray-500 text-white"
                }`}
              >
                <TbCurrencyNaira />
              </div>
              <div
                onClick={toggleCurrency}
                className={`${CurrentBtnStyle} ml-2 ${
                  currency === "dollar" && "bg-gray-500 text-white"
                }`}
              >
                <TbCurrencyDollar />
              </div>
            </div>
            <p className="w-full text-right mt-3 text-xl truncate">
              {" "}
              {range} {currency === "dollar" ? "USD" : "NGN"}
            </p>
          </div>
          <Controller
            control={control}
            name="state"
            render={({ field: { value, onChange } }) => (
              <SelectField
                value={value}
                label="Location"
                showSearch={true}
                placeholder="Select State"
                error={errors.state?.message}
                onChange={(val) => {
                  setEstimation(0);
                  onChange(val);
                }}
                data={StatesAndCities.map(({ name }) => ({ value: name }))}
              />
            )}
          />

          <div className="my-7" />
          <div>
            <label className="text-gray-400 ">Select Building Type</label>
            <div className={flexer + "mt-1"}>
              <Building
                divKey="123"
                Svg={Bungalow}
                Svg2={Bungalow2}
                label="bungalow"
                labelKey="bungalow"
              />
              <Building
                divKey="456"
                Svg={Duplex}
                Svg2={Duplex2}
                label="duplex"
                labelKey="duplex"
              />
              <Building
                divKey="789"
                Svg={TownHouse}
                Svg2={TownHouse2}
                labelKey="terrace"
                label="Terrace"
              />
            </div>
          </div>
          <div className="flex mt-6">
            <InputField
              showDimensions
              value={watch("bedRoom")}
              {...{ toggleDimType, dimType }}
              onDimensionsSubmit={(val) =>
                setDimensions((prev) => ({ ...prev, bedRoom: val }))
              }
              error={errors.bedRoom?.message}
              register={register("bedRoom")}
              label="Bedroom"
              placeholder="e.g 2"
              type="number"
              maximum={6}
            />
            <div className="spacer" />
            <InputField
              showDimensions
              value={watch("toiletBath")}
              {...{ toggleDimType, dimType }}
              onDimensionsSubmit={(val) =>
                setDimensions((prev) => ({ ...prev, toiletBath: val }))
              }
              error={errors.toiletBath?.message}
              register={register("toiletBath")}
              label="Toilet / Bath"
              placeholder="e.g 2"
              type="number"
              maximum={6}
            />
          </div>
          <div className="flex mt-5">
            <InputField
              showDimensions
              value={watch("livingRoom")}
              {...{ toggleDimType, dimType }}
              onDimensionsSubmit={(val) =>
                setDimensions((prev) => ({ ...prev, livingRoom: val }))
              }
              error={errors.livingRoom?.message}
              register={register("livingRoom")}
              label="living room"
              placeholder="e.g 2"
              type="number"
              maximum={2}
            />
            <div className="spacer" />
            <InputField
              showDimensions
              value={watch("kitchen")}
              {...{ toggleDimType, dimType }}
              onDimensionsSubmit={(val) =>
                setDimensions((prev) => ({ ...prev, kitchen: val }))
              }
              error={errors.kitchen?.message}
              register={register("kitchen")}
              placeholder="e.g 2"
              label="Kitchen"
              type="number"
              maximum={2}
            />
          </div>
          <Button
            buttonType="submit"
            text="Get Estimate"
            isLoading={isLoading}
            disabled={!!estimation}
            className="mt-6 mb-0 w-full"
            type={!isValid || estimation ? "muted" : "primary"}
          />
          {estimation ? (
            <div className={flexer}>
              <Button
                type="transparent"
                className="mt-2"
                text="Clear Form"
                onClick={resetForm}
              />
              <Button
                className="mt-2"
                type="secondary"
                text="Create project"
                onClick={() =>
                  window.open(
                    `https://auth.${process.env.REACT_APP_URL}/persona?type=signup`,
                    "_self"
                  )
                }
              />
            </div>
          ) : null}
        </form>
      </section>
    </div>
  );
}
